import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker/ItemPicker'
import {SharedEmptyPicker} from '@github-ui/item-picker/SharedEmptyPicker'
import {SharedPicker} from '@github-ui/item-picker/SharedPicker'
import {testIdProps} from '@github-ui/test-id-props'
import type {ColorName} from '@github-ui/use-named-color'
import {SingleSelectIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useCallback, useState} from 'react'

import type {PersistedOption} from '../../api/columns/contracts/single-select'
import {StatusUpdatesResources} from '../../strings'
import {ColorDecorator} from '../fields/single-select/color-decorator'

export type Status = {
  id: string
  name: string
  nameHtml: string
  color: ColorName
  description: string
}

const itemPickerContainerStyle: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 1,
}

type StatusPickerProps = {
  defaultStatus?: PersistedOption | null
  onChange: (status: PersistedOption | null) => void
  options: Array<PersistedOption>
}

export const StatusPicker = ({defaultStatus, onChange, options}: StatusPickerProps) => {
  const [filter, setFilter] = useState('')
  const [localSelectedStatus, setLocalSelectedStatus] = useState<PersistedOption | null>(defaultStatus ?? null)

  const filteredStatuses = options.filter(s => s.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  const getItemKey = useCallback((status: PersistedOption) => status.id, [])

  const handleStatusChange = useCallback(
    ([selectedStatus]: Array<PersistedOption>) => {
      setLocalSelectedStatus(selectedStatus ?? null)
      onChange(selectedStatus ?? null)
    },
    [onChange],
  )

  const convertToItemProps = useCallback((status: PersistedOption): ExtendedItemProps<PersistedOption> => {
    return {
      id: status.id,
      description: status.description,
      descriptionVariant: 'block',
      text: status.name,
      source: status,
      leadingVisual: () => <ColorDecorator color={status.color} />,
    }
  }, [])

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (localSelectedStatus) {
        return (
          <SharedPicker
            anchorProps={anchorProps}
            readonly={false}
            label={StatusUpdatesResources.statusFieldLabel}
            sharedPickerMainValue={localSelectedStatus.name}
            leadingIconElement={<ColorDecorator color={localSelectedStatus.color} />}
            ariaLabel={StatusUpdatesResources.statusPickerAriaLabel}
          />
        )
      }

      return (
        <SharedEmptyPicker
          label={StatusUpdatesResources.statusFieldLabel}
          leadingIcon={SingleSelectIcon}
          anchorProps={anchorProps}
        />
      )
    },
    [localSelectedStatus],
  )

  return (
    <Box sx={itemPickerContainerStyle} {...testIdProps('status-updates-status-picker')}>
      <ItemPicker
        items={filteredStatuses}
        initialSelectedItems={localSelectedStatus ? [localSelectedStatus] : []}
        filterItems={setFilter}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={StatusUpdatesResources.statusPickerFilterPlaceholderText}
        selectionVariant="single"
        onSelectionChange={handleStatusChange}
        renderAnchor={renderAnchor}
        resultListAriaLabel={StatusUpdatesResources.statusPickerResultsAriaLabel}
        title={StatusUpdatesResources.statusPickerTitle}
      />
    </Box>
  )
}

try{ StatusPicker.displayName ||= 'StatusPicker' } catch {}